import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { ChevronDown, Undo, Redo, Bold, Italic, Underline, Strikethrough, Link, List, AlignLeft, AlertCircle } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getPlantcodes, getLocation, getStatus, getKeys } from '../../services/locationService';
import { saveEarths, updateEarth } from '../../services/hvWorkPermitService';

const isRequirementEnabled = (value: unknown): boolean =>
  value === true || value === 1 || (typeof value === 'string' && ['yes', 'true', '1'].includes(value.toLowerCase()));

const validationSchema = Yup.object({
  plantCode: Yup.number().required('Plant Code is required'),
  location: Yup.string().required('Location is required'),
  description: Yup.string().max(500, 'Description must be 500 characters or less'),
  status: Yup.string().required('Status is required'),
  key: Yup.string().required('Key is required'),
  ops: Yup.string().required('OPS is required'),
});

interface PlantCode {
  id: number;
  title: string;
}

interface Location {
  id: number;
  title: string;
}

interface Status {
  id: number;
  title: string;
}

interface Key {
  id: number;
  title: string;
}

interface EarthProps {
  permitId: number | null;
  onDataChange?: (data: any) => void;
  onValidationChange?: (isValid: boolean) => void;
  triggerSubmit?: number;
  onSubmitSuccess?: (data: any) => void;
  existingEarth?: any[];
  earthsSaved?: boolean;
  onSessionEarthChange?: (items: any[]) => void;
  draftValues?: any;
  onDraftChange?: (values: any) => void;
}

const Earths = forwardRef<any, EarthProps>(
  (
    {
      permitId,
      onDataChange,
      onValidationChange,
      triggerSubmit = 0,
      onSubmitSuccess,
      existingEarth = [],
      earthsSaved = false,
      onSessionEarthChange,
      draftValues,
      onDraftChange,
    },
    ref,
  ) => {
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const [plantCodes, setPlantCodes] = useState<PlantCode[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [status, setStatus] = useState<Status[]>([]);
    const [keys, setKeys] = useState<Key[]>([]);
    const [loading, setLoading] = useState(true);

    const emptyEarths = {
      id: null as number | null,
      hv_work_permit_id: null as number | null,
      plant_code: null as PlantCode | null,
      plant_code_id: '',
      location: null as Location | null,
      location_id: '',
      description: '',
      requirements: false,
      items: [] as any[],
    };
    const [currentEarth, setCurrentEarth] = useState<any>(emptyEarths);

    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

    const [editingItemId, setEditingItemId] = useState<number | null>(null);
    const nextTemporaryItemIdRef = useRef(-1);
    const formRef = useRef<HTMLFormElement>(null);
    const addButtonRef = useRef<HTMLButtonElement>(null);

    const lastSyncedEarthsRef = useRef<string>('');
    const existingItemsRef = useRef<any[]>([]);
    existingItemsRef.current = Array.isArray(existingEarth?.[0]?.items) ? existingEarth[0].items : [];
    const [validationError, setValidationError] = useState<string | null>(null);

    const emptyValues = {
      id: '',
      plantCode: '',
      location: '',
      description: '',
      requirements: false,
      status: '',
      key: '',
      ops: '',
    };

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          const [plantCodesData, locationsData, statusData, keysData] = await Promise.all([
            getPlantcodes(),
            getLocation(),
            getStatus(),
            getKeys(),
          ]);
          setPlantCodes(plantCodesData);
          setLocations(locationsData);
          setStatus(statusData);
          setKeys(keysData);
        } catch (error) {
          console.error('Failed to fetch data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, []);

    useEffect(() => {
      if (existingEarth?.length) {
        const iso = existingEarth[0];

        const signature = JSON.stringify({
          id: iso.id ?? null,
          plant_code_id: iso.plant_code_id || '',
          location_id: iso.location_id || '',
          description: iso.description || '',
          requirements: iso.requirements || false,
          items: iso.items || [],
        });

        if (signature === lastSyncedEarthsRef.current) {
          return;
        }
        lastSyncedEarthsRef.current = signature;

        setCurrentEarth({
          id: iso.id ?? null,
          hv_work_permit_id: iso.hv_work_permit_id ?? null,
          plant_code: iso.plant_code ?? null,
          plant_code_id: iso.plant_code_id || '',
          location: iso.location ?? null,
          location_id: iso.location_id || '',
          description: iso.description || '',
          requirements: iso.requirements || false,
          items: Array.isArray(iso.items)
            ? iso.items.map((item: any) => ({
                ...item,
                status: item.status_id?.toString() || item.status?.toString() || '',
                key: item.key_id?.toString() || item.key?.toString() || '',
              }))
            : [],
        });
        if (editingItemId === null) {
          formik.setValues({
            id: '',
            plantCode: iso.plant_code_id?.toString() || '',
            location: iso.location_id?.toString() || '',
            description: iso.description || '',
            requirements: isRequirementEnabled(iso.requirements),
            status: '',
            key: '',
            ops: '',
          });
        }
      }
    }, [existingEarth]);

    useEffect(() => {
      if (onSessionEarthChange) {
        // Do not let the initial empty state overwrite rows restored by the
        // parent when the user navigates back to this step.
        if (existingItemsRef.current.length > 0 && currentEarth.items.length === 0) {
          return;
        }

        lastSyncedEarthsRef.current = JSON.stringify({
          id: currentEarth.id ?? null,
          plant_code_id: currentEarth.plant_code_id || '',
          location_id: currentEarth.location_id || '',
          description: currentEarth.description || '',
          requirements: currentEarth.requirements || false,
          items: currentEarth.items || [],
        });

        onSessionEarthChange([currentEarth]);
      }
    }, [currentEarth]);

    useEffect(() => {
      if (triggerSubmit > 0 && onSubmitSuccess) {
        const saveAllIsolations = async () => {
          try {
            setValidationError(null);

            if (editingItemId !== null) {
              setValidationError('You have an unsaved edit in progress. Please click "Update" or "Cancel Edit" before proceeding.');
              return;
            }

            if (!permitId) {
              console.error('Permit ID is required');
              return;
            }

            if (currentEarth.items.length === 0) {
              setValidationError('At least one valid operational instruction is required. Please add an item before proceeding.');
              return;
            }

            const items = currentEarth.items.map((item: any) => ({
              status_id: Number(item.status_id ?? item.status),
              key_id: Number(item.key_id ?? item.key),
              ops: item.ops,
            }));

            const payload = {
              hv_work_permit_id: permitId,
              plant_code_id: Number(formik.values.plantCode),
              location_id: Number(formik.values.location),
              describe_apparatus: formik.values.description,
              requirements: formik.values.requirements,
              items: items,
            };
            let response;
            if (earthsSaved && currentEarth.id) {
              response = await updateEarth(currentEarth.id, payload);
            } else {
              response = await saveEarths(payload);
            }

            const savedId = response?.data?.id || response?.data?.[0]?.id || currentEarth.id;
            const updatedEarth = {
              ...currentEarth,
              id: savedId,
              plant_code: plantCodes.find((code) => code.id === Number(formik.values.plantCode)) || null,
              plant_code_id: formik.values.plantCode,
              location: locations.find((location) => location.id === Number(formik.values.location)) || null,
              location_id: formik.values.location,
              description: formik.values.description,
              requirements: formik.values.requirements,
            };
            setCurrentEarth(updatedEarth);

            onSubmitSuccess(updatedEarth);
          } catch (error) {
            console.error('Failed to save isolations:', error);
          }
        };

        saveAllIsolations();
      }
    }, [triggerSubmit, editingItemId]);

    const formik = useFormik({
      initialValues: emptyValues,
      enableReinitialize: false,
      validationSchema,
      onSubmit: async (values, { setSubmitting, resetForm }) => {
        try {
          setSubmitError(null);
          setSubmitSuccess(false);

          if (!permitId) {
            setSubmitError('Permit ID is required to create an isolation');
            setSubmitting(false);
            return;
          }

          const itemPayload = {
            plant_code_id: Number(values.plantCode),
            location_id: Number(values.location),
            description: values.description,
            requirements: values.requirements,
            status: values.status,
            key: values.key,
            ops: values.ops,
          };

          let response;

          const activeEditId = editingItemId || (values.id ? Number(values.id) : null);

          if (activeEditId) {
            const updatePayload = {
              hv_work_permit_id: permitId,
              ...itemPayload,
            };
            response = await updateEarth(activeEditId, updatePayload);
          } else {
            const createPayload = {
              hv_work_permit_id: permitId,
              items: [itemPayload],
            };
            response = await saveEarths(createPayload);
          }

          const plantCodeDetails = plantCodes.find((pc) => pc.id === Number(values.plantCode));
          const locationDetails = locations.find((loc) => loc.id === Number(values.location));

          const itemData = {
            id: response.data?.[0]?.id || response.data?.id || activeEditId,
            plant_code_id: Number(values.plantCode),
            plant_code: plantCodeDetails,
            location_id: Number(values.location),
            location: locationDetails,
            describe_apparatus: values.description,
            requirements: values.requirements,
            req: values.requirements ? 'Yes' : 'No',
            status: values.status,
            key: values.key,
            ops: values.ops,
          };

          setCurrentEarth((prev: any) => {
            const items = activeEditId
              ? prev.items.map((it: any) => (Number(it.id) === Number(activeEditId) ? itemData : it))
              : [...prev.items, itemData];
            const firstItem = items[0];

            return {
              ...prev,
              plant_code: firstItem.plant_code,
              plant_code_id: firstItem.plant_code_id,
              location: firstItem.location,
              location_id: firstItem.location_id,
              description: firstItem.describe_apparatus,
              requirements: firstItem.requirements,
              items,
            };
          });

          if (activeEditId) {
            setEditingItemId(null);
          }

          if (onDataChange) {
            onDataChange(values);
          }

          setSubmitSuccess(true);
          resetForm();

          setTimeout(() => {
            setSubmitSuccess(false);
          }, 3000);
        } catch (error: any) {
          console.error('Failed to save isolation:', error);
          setSubmitError(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to save isolation. Please try again.',
          );
        } finally {
          setSubmitting(false);
        }
      },
    });

    const hasHydratedDraft = useRef(false);
    useEffect(() => {
      if (hasHydratedDraft.current) return;

      const hasDraft =
        draftValues &&
        (draftValues.id ||
          draftValues.plantCode ||
          draftValues.location ||
          draftValues.description ||
          draftValues.requirements ||
          draftValues.status ||
          draftValues.key ||
          draftValues.ops);

      if (!hasDraft) {
        hasHydratedDraft.current = true;
        return;
      }

      const draftId = draftValues.id ? Number(draftValues.id) : null;
      const belongsToCurrent = draftId !== null && currentEarth.items.some((it: any) => Number(it.id) === draftId);

      if (draftId === null || belongsToCurrent) {
        formik.setValues(draftValues);
        if (draftId !== null) setEditingItemId(draftId);
        hasHydratedDraft.current = true;
      }
    }, [currentEarth.items, draftValues]);

    useEffect(() => {
      onDraftChange?.(formik.values);
    }, [formik.values]);

    // CHANGED: fix type-mismatch bug — activeEditId is always a Number,
    // but item.id can be a string (e.g. hydrated from formik values via
    // handleEditItem, which stores id as item.id?.toString()). A strict
    // `it.id === activeEditId` comparison silently fails in that case,
    // so the item never gets replaced in place on the first click.
    // Normalizing both sides with Number(...) before comparing fixes it.
    const handleAddEarth = async () => {
      const values = formik.values;

      const errors = await formik.validateForm();
      await formik.setTouched({
        plantCode: true,
        location: true,
        status: true,
        key: true,
        ops: true,
      });

      if (Object.keys(errors).length > 0) {
        return;
      }

      const activeEditId = editingItemId ?? (values.id ? Number(values.id) : null);
      const itemId = activeEditId ?? nextTemporaryItemIdRef.current--;

      const plantCodeDetails = plantCodes.find((pc) => pc.id === Number(values.plantCode));
      const locationDetails = locations.find((loc) => loc.id === Number(values.location));

      const itemData = {
        id: itemId,
        status_id: Number(values.status),
        status: values.status,
        key_id: Number(values.key),
        key: values.key,
        ops: values.ops,
      };

      setCurrentEarth((prev: any) => {
        const items =
          activeEditId !== null
            ? prev.items.map((it: any) => (Number(it.id) === Number(activeEditId) ? itemData : it))
            : [...prev.items, itemData];
        return {
          ...prev,
          plant_code: plantCodeDetails,
          plant_code_id: values.plantCode,
          location: locationDetails,
          location_id: values.location,
          description: values.description,
          requirements: values.requirements,
          items,
        };
      });

      if (activeEditId !== null) {
        setEditingItemId(null);
      }

      setValidationError(null);

      const clearedValues = {
        ...emptyValues,
        plantCode: values.plantCode,
        location: values.location,
        description: values.description,
        requirements: values.requirements,
      };
      formik.setValues(clearedValues);
      onDraftChange?.(clearedValues);

      formik.setTouched(
        {
          ...formik.touched,
          plantCode: false,
          location: false,
          status: false,
          key: false,
          ops: false,
        },
        false,
      );
    };

    const handleEditItem = (item: any) => {
      setEditingItemId(Number(item.id));

      formik.setValues({
        id: item.id?.toString() || '',
        plantCode: formik.values.plantCode || currentEarth.plant_code_id?.toString() || '',
        location: formik.values.location || currentEarth.location_id?.toString() || '',
        description: formik.values.description || currentEarth.description || '',
        requirements: formik.values.requirements,
        status: item.status_id?.toString() || item.status?.toString() || '',
        key: item.key_id?.toString() || item.key?.toString() || '',
        ops: item.ops || '',
      });

      formik.setTouched(
        {
          plantCode: false,
          location: false,
          status: false,
          key: false,
          ops: false,
        },
        false,
      );

      setValidationError(null);
      setSubmitError(null);
      setExpandedItem(null);
    };

    const handleCancelEdit = () => {
      setEditingItemId(null);
      formik.setValues({
        ...emptyValues,
        plantCode: formik.values.plantCode,
        location: formik.values.location,
        description: formik.values.description,
        requirements: formik.values.requirements,
      });
    };

    useImperativeHandle(ref, () => ({
      validateForm: async () => {
        setValidationError(null);

        const hasInstructionDraft = Boolean(formik.values.id || formik.values.status || formik.values.key || formik.values.ops);

        const mustValidateInstruction = currentEarth.items.length === 0 || editingItemId !== null || hasInstructionDraft;
        const errors = await formik.validateForm();
        await formik.setTouched(
          {
            plantCode: true,
            location: true,
            description: true,
            status: mustValidateInstruction,
            key: mustValidateInstruction,
            ops: mustValidateInstruction,
          },
          false,
        );

        const fieldsToValidate = ['plantCode', 'location', 'description', ...(mustValidateInstruction ? ['status', 'key', 'ops'] : [])];
        const firstInvalidField = fieldsToValidate.find((field) => errors[field]);

        if (firstInvalidField) {
          requestAnimationFrame(() => {
            const field = formRef.current?.querySelector<HTMLElement>(`[name="${firstInvalidField}"]`);
            field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            field?.focus({ preventScroll: true });
          });
          return false;
        }

        if (editingItemId !== null) {
          setValidationError('You have an unsaved edit in progress. Please click "Update" or "Cancel Edit" before proceeding.');
          requestAnimationFrame(() => addButtonRef.current?.focus());
          return false;
        }

        if (currentEarth.items.length === 0) {
          setValidationError('At least one valid operational instruction is required. Please add an item before proceeding.');
          requestAnimationFrame(() => addButtonRef.current?.focus());
          return false;
        }

        if (hasInstructionDraft) {
          setValidationError('Please add the completed earth instruction before proceeding.');
          requestAnimationFrame(() => addButtonRef.current?.focus());
          return false;
        }

        return true;
      },
      getValues: () => ({
        ...currentEarth,
        plant_code: plantCodes.find((code) => code.id === Number(formik.values.plantCode)) || currentEarth.plant_code,
        plant_code_id: formik.values.plantCode,
        location: locations.find((location) => location.id === Number(formik.values.location)) || currentEarth.location,
        location_id: formik.values.location,
        description: formik.values.description,
        requirements: formik.values.requirements,
        items: currentEarth.items,
      }),
    }));

    const getStatusColor = (status: string) => {
      const statusLower = status?.toLowerCase() || '';
      if (statusLower === 'in progress') return { dot: 'bg-orange-500', text: 'text-orange-500' };
      if (statusLower === 'completed') return { dot: 'bg-green-500', text: 'text-green-500' };
      if (statusLower === 'pending') return { dot: 'bg-gray-500', text: 'text-gray-500' };
      if (statusLower === 'on hold') return { dot: 'bg-yellow-500', text: 'text-yellow-500' };
      return { dot: 'bg-gray-500', text: 'text-gray-500' };
    };

    const isAddDisabled =
      formik.isSubmitting ||
      !formik.values.plantCode ||
      !formik.values.location ||
      !formik.values.status ||
      !formik.values.key ||
      !formik.values.ops;

    return (
      <div className="max-w-7xl mx-auto bg-white">
        <form
          ref={formRef}
          onSubmit={(event) => {
            event.preventDefault();
            void handleAddEarth();
          }}
          className="space-y-6"
        >
          {submitSuccess && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium">✓ Isolation {editingItemId ? 'updated' : 'created'} successfully!</p>
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">✕ {submitError}</p>
            </div>
          )}

          {/* Edit mode banner */}
          {editingItemId && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <p className="text-sm text-blue-800 font-medium">✏️ Editing this earth entry</p>
              <button type="button" onClick={handleCancelEdit} className="text-sm text-blue-600 underline hover:text-blue-800">
                Cancel Edit
              </button>
            </div>
          )}

          <div>
            <h3 className="text-base font-semibold text-gray-900">Earth Details</h3>
            <p className="text-sm text-gray-500 mt-1">These details apply to every operational instruction below.</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Plant Code <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="plantCode"
                  value={formik.values.plantCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading || formik.isSubmitting}
                  className={`w-full px-4 py-2.5 border ${
                    formik.touched.plantCode && formik.errors.plantCode ? 'border-red-500' : 'border-gray-300'
                  } rounded appearance-none bg-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 ${
                    !formik.values.plantCode ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  <option value="">Select Plant Code</option>
                  {plantCodes.map((plantCode: PlantCode) => (
                    <option key={plantCode.id} value={plantCode.id}>
                      {plantCode.title}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {formik.touched.plantCode && formik.errors.plantCode && (
                <div className="text-red-500 text-sm mt-1">{String(formik.errors.plantCode)}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Select Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading || formik.isSubmitting}
                  className={`w-full px-4 py-2.5 border ${
                    formik.touched.location && formik.errors.location ? 'border-red-500' : 'border-gray-300'
                  } rounded appearance-none bg-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 ${
                    !formik.values.location ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  <option value="">Select Location</option>
                  {locations.map((location: Location) => (
                    <option key={location.id} value={location.id}>
                      {location.title}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {formik.touched.location && formik.errors.location && (
                <div className="text-red-500 text-sm mt-1">{String(formik.errors.location)}</div>
              )}
            </div>
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-900 mb-2">
              Describe Apparatus/Instructions
              <span className="ml-2 text-gray-400 cursor-help">ⓘ</span>
            </label>

            <div className="border border-gray-300 rounded-t bg-gray-100 px-3 py-2 flex items-center space-x-2">
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Undo className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Redo className="w-4 h-4 text-gray-600" />
              </button>

              <div className="relative">
                <button
                  type="button"
                  className="px-3 py-1.5 hover:bg-gray-200 rounded flex items-center text-sm text-gray-700"
                  disabled={formik.isSubmitting}
                >
                  Normal
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>
              </div>

              <div className="relative">
                <button type="button" className="p-1.5 hover:bg-gray-200 rounded flex items-center" disabled={formik.isSubmitting}>
                  <AlignLeft className="w-4 h-4 text-gray-600" />
                  <ChevronDown className="w-3 h-3 ml-1 text-gray-600" />
                </button>
              </div>

              <div className="relative">
                <button
                  type="button"
                  className="p-1.5 hover:bg-gray-200 rounded flex items-center bg-black text-white"
                  disabled={formik.isSubmitting}
                >
                  <div className="w-4 h-4 bg-black rounded-sm"></div>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>
              </div>

              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Bold className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Italic className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Underline className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Strikethrough className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Link className="w-4 h-4 text-gray-600" />
              </button>

              <div className="relative">
                <button type="button" className="p-1.5 hover:bg-gray-200 rounded flex items-center" disabled={formik.isSubmitting}>
                  <List className="w-4 h-4 text-gray-600" />
                  <ChevronDown className="w-3 h-3 ml-1 text-gray-600" />
                </button>
              </div>
            </div>

            <textarea
              name="description"
              value={formik.values.description}
              maxLength={500}
              onChange={(e) => {
                if (e.target.value.length > 500) return;
                formik.handleChange(e);
              }}
              onBlur={formik.handleBlur}
              placeholder="Add description here"
              rows={6}
              className="w-full px-4 py-3 border border-t-0 border-gray-300 rounded-b focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100"
              disabled={formik.isSubmitting}
            />
            <div className="flex justify-between items-center mt-1">
              {formik.touched.description && formik.errors.description ? (
                <div className="text-red-500 text-sm">{String(formik.errors.description)}</div>
              ) : (
                <div />
              )}
              <div className={`text-xs ml-auto ${500 - (formik.values.description?.length || 0) <= 50 ? 'text-red-500' : 'text-gray-400'}`}>
                {formik.values.description?.length || 0}/500
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="requirements"
              id="requirements"
              checked={formik.values.requirements}
              onChange={formik.handleChange}
              disabled={formik.isSubmitting}
              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <label htmlFor="requirements" className="ml-3 text-sm font-medium text-gray-900">
              Requirements
            </label>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-base font-semibold text-gray-900">Operational Instructions</h3>
            <p className="text-sm text-gray-500 mt-1">Add one or more Status, Key, and OPS entries.</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Select Status <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  className={`w-full px-4 py-2.5 border ${
                    formik.touched.status && formik.errors.status ? 'border-red-500' : 'border-gray-300'
                  } rounded appearance-none bg-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 ${
                    !formik.values.status ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  <option value="">Select Status</option>
                  {status && status.length > 0 ? (
                    status.map((stat: any) => (
                      <option key={stat.id} value={stat.id}>
                        {stat.title || stat.name || stat.status}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading statuses...</option>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {formik.touched.status && formik.errors.status && (
                <div className="text-red-500 text-sm mt-1">{String(formik.errors.status)}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Key <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="key"
                  value={formik.values.key}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  className={`w-full px-4 py-2.5 border ${
                    formik.touched.key && formik.errors.key ? 'border-red-500' : 'border-gray-300'
                  } rounded appearance-none bg-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 ${
                    !formik.values.key ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  <option value="">Select Key</option>
                  {keys && keys.length > 0 ? (
                    keys.map((key: any) => (
                      <option key={key.id} value={key.id}>
                        {key.title || key.name || key.key}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading keys...</option>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {formik.touched.key && formik.errors.key && <div className="text-red-500 text-sm mt-1">{String(formik.errors.key)}</div>}
            </div>
          </div>

          <div className="max-w-[calc(50%-12px)]">
            <label className="flex items-center text-sm font-medium text-gray-900 mb-2">
              Enter OPS <span className="text-red-500">*</span>
              <span className="ml-2 text-gray-400 cursor-help">ⓘ</span>
            </label>
            <input
              type="text"
              name="ops"
              value={formik.values.ops}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter OPS"
              disabled={formik.isSubmitting}
              className={`w-full px-4 py-2.5 border ${
                formik.touched.ops && formik.errors.ops ? 'border-red-500' : 'border-gray-300'
              } rounded focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100`}
            />
            {formik.touched.ops && formik.errors.ops && <div className="text-red-500 text-sm mt-1">{String(formik.errors.ops)}</div>}
          </div>

          {/* Validation Error Message - At least one item required */}
          {validationError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 font-medium">{validationError}</p>
            </div>
          )}

          <div>
            <button
              ref={addButtonRef}
              type="button"
              onClick={handleAddEarth}
              disabled={isAddDisabled}
              className={`px-6 py-2.5 border-2 rounded font-medium ${
                isAddDisabled
                  ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                  : 'bg-white text-black border-black hover:bg-green-50'
              }`}
            >
              {editingItemId ? 'Update' : 'Add'}
            </button>
          </div>

          <div className="space-y-3 mt-4">
            {currentEarth.items.map((item: any, index: number) => {
              const statusLabel =
                status.find((s: any) => s.id === Number(item.status_id ?? item.status))?.title || item.status || 'Pending';

              const keyLabel = keys.find((k: any) => k.id === Number(item.key_id ?? item.key))?.title || item.key || 'N/A';

              const colors = getStatusColor(statusLabel);
              const itemKey = `earth-item-${item.id ?? index}`;

              return (
                <div key={itemKey} className="border border-gray-300 rounded-md">
                  {/* Header */}
                  <div className="px-4 py-3 flex items-center justify-between">
                    {/* Left side - expand */}
                    <button
                      type="button"
                      onClick={() => setExpandedItem(expandedItem === itemKey ? null : itemKey)}
                      className="flex-1 flex items-center text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {index + 1}. {item.ops || 'Operational Instruction'}
                        </span>

                        <span className="flex items-center space-x-1.5">
                          <span className={`w-2 h-2 rounded-full ${colors.dot}`} />

                          <span className={`text-sm ${colors.text} font-normal`}>{statusLabel}</span>
                        </span>
                      </div>

                      <ChevronDown
                        className={`w-5 h-5 ml-auto mr-3 text-gray-600 transition-transform ${
                          expandedItem === itemKey ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* EDIT BUTTON OUTSIDE */}
                    <button
                      type="button"
                      onClick={() => handleEditItem(item)}
                      disabled={formik.isSubmitting || (editingItemId !== null && Number(editingItemId) !== Number(item.id))}
                      className={`px-4 py-1.5 text-sm border rounded transition-colors ${
                        editingItemId !== null && Number(editingItemId) !== Number(item.id)
                          ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                          : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      {editingItemId === Number(item.id) ? 'Editing' : 'Edit'}
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {expandedItem === itemKey && (
                    <div className="px-4 py-3 border-t border-gray-300 bg-gray-50">
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-600 break-words">
                          <span className="font-medium">OPS:</span> {item.ops || 'N/A'}
                        </p>

                        <p className="text-gray-600">
                          <span className="font-medium">Key:</span> {keyLabel}
                        </p>

                        <p className="text-gray-600">
                          <span className="font-medium">Status:</span> {statusLabel}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </form>
      </div>
    );
  },
);

Earths.displayName = 'Earths';

export default Earths;
